import { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, X, ShieldCheck, Zap, BatteryCharging, Sun } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const ACCEPTED_ATTR =
  '.pdf,.html,.htm,.jpg,.jpeg,.png,.webp,application/pdf,text/html,image/jpeg,image/png,image/webp';
const MAX_MB = 15;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const CARD_MOTION_INITIAL = { opacity: 0, y: 12 };
const CARD_MOTION_ANIMATE = { opacity: 1, y: 0 };
const CARD_MOTION_TRANSITION = { duration: 0.35 };

const TRUST_BADGES = [
  { icon: FileText, text: 'PDF, HTML or photo' },
  { icon: ShieldCheck, text: 'Auto-detects state' },
  { icon: BatteryCharging, text: 'TOD-aware BESS' },
];

const PROCESS_STEPS = [
  { n: 1, t: 'Upload bill' },
  { n: 2, t: 'AI extracts + auto-detects' },
  { n: 3, t: 'See recommendations' },
];

function validateBillFile(file) {
  if (!file) return 'No file selected';
  const nameLower = (file.name || '').toLowerCase();
  const isPdf = file.type === 'application/pdf' || nameLower.endsWith('.pdf');
  const isHtml =
    file.type === 'text/html' ||
    file.type === 'application/xhtml+xml' ||
    nameLower.endsWith('.html') ||
    nameLower.endsWith('.htm');
  // Most customers hold a paper bill, so a phone photo has to work too.
  const isImage =
    ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
    /\.(jpe?g|png|webp)$/.test(nameLower);
  if (!isPdf && !isHtml && !isImage) return 'Please upload a PDF, HTML or a photo (JPG/PNG) of your bill';
  if (file.size > MAX_BYTES) return `File is larger than ${MAX_MB} MB`;
  return null;
}

export const UploadState = ({ onSubmit }) => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const inputRef = useRef(null);

  const acceptFile = useCallback((f) => {
    const err = validateBillFile(f);
    if (err) {
      toast.error(err);
      setShakeKey((k) => k + 1);
      return;
    }
    setFile(f);
    toast.success(`Bill selected: ${f.name}`);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const onBrowse = () => inputRef.current?.click();

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
  };

  const canSubmit = file && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      setBusy(true);
      await onSubmit({ file });
    } catch (err) {
      toast.error(err?.message || 'Failed to upload bill');
      setBusy(false);
    }
  };

  return (
    <div className="hero-bg relative" data-testid="upload-state-root">
      <div className="noise-overlay absolute inset-0" aria-hidden />
      <div className="relative mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 sm:py-14 lg:grid-cols-2 lg:gap-12 lg:px-8">
        {/* Left copy */}
        <div className="flex flex-col justify-center">
          <Badge
            className="mb-4 w-max border border-primary/20 bg-accent text-accent-foreground"
            data-testid="hero-eyebrow"
          >
            <Zap className="mr-1.5 h-3.5 w-3.5" /> Solar + Battery sizing for Indian tariffs
          </Badge>
          <h1
            className="font-display text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl"
            data-testid="upload-hero-headline"
          >
            Turn your electricity bill into a solar &amp; battery savings plan.
          </h1>
          <p className="mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">
            Just upload your monthly bill. We auto-detect your state, DISCOM and consumer type,
            extract TOD consumption, and size your solar &amp; BESS with annual savings — in seconds.
          </p>

          <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="trust-badges">
            {TRUST_BADGES.map(({ icon: Icon, text }) => (
              <div
                key={text}
                className="flex items-center gap-2 rounded-xl border border-border/70 bg-white/70 px-3 py-2 text-sm text-foreground shadow-sm"
              >
                <Icon className="h-4 w-4 text-primary" />
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 hidden gap-6 sm:flex" data-testid="process-steps">
            {PROCESS_STEPS.map(({ n, t }) => (
              <div key={n} className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground tabular">
                  {n}
                </span>
                <span className="text-sm text-muted-foreground">{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: analyzer card */}
        <motion.div
          initial={CARD_MOTION_INITIAL}
          animate={CARD_MOTION_ANIMATE}
          transition={CARD_MOTION_TRANSITION}
        >
          <Card
            className="rounded-2xl border border-border/70 bg-card p-4 shadow-[0_1px_0_rgba(15,23,42,0.04),0_10px_36px_rgba(15,23,42,0.06)] sm:p-6"
            data-testid="analyzer-card"
          >
            <div className="mb-4 flex items-center gap-2">
              <Sun className="h-5 w-5 text-primary" />
              <h2 className="font-display text-lg font-semibold">Analyze your bill</h2>
            </div>

            <div
              key={shakeKey}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={[
                'relative rounded-2xl border border-dashed p-8 transition-colors duration-200',
                dragOver
                  ? 'border-ring bg-accent ring-2 ring-ring ring-offset-2'
                  : 'border-border bg-white hover:border-ring hover:bg-accent/60',
                shakeKey > 0 && !file ? 'shake-x' : '',
              ].join(' ')}
              data-testid="bill-upload-dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_ATTR}
                className="sr-only"
                onChange={onSelect}
                data-testid="bill-upload-file-input"
              />
              {!file ? (
                <button
                  type="button"
                  onClick={onBrowse}
                  className="flex w-full flex-col items-center justify-center gap-3 text-center"
                  data-testid="bill-upload-browse-button"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Upload className="h-6 w-6" />
                  </span>
                  <span className="font-medium">Drop your bill here or click to browse</span>
                  <span className="text-sm text-muted-foreground">
                    PDF, HTML or a clear photo · up to {MAX_MB} MB · we auto-detect everything
                  </span>
                </button>
              ) : (
                <div
                  className="flex items-center justify-between gap-3"
                  data-testid="bill-upload-selected-file"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FileText className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {file.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground"
                    data-testid="bill-upload-remove-file-button"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <Button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="mt-5 h-12 w-full rounded-xl text-base font-semibold transition-colors duration-200 hover:bg-[hsl(var(--brand-blue-2))] active:scale-[0.99]"
              data-testid="analyze-bill-button"
            >
              {busy ? 'Uploading…' : 'Analyze bill'}
            </Button>

            <p className="mt-3 text-xs text-muted-foreground" data-testid="privacy-note">
              We use your bill only for this analysis. No spam — our team may follow up with a
              detailed proposal.
            </p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default UploadState;
